import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import arrowIcon from '@/assets/dashboard/arrow.svg'
import { BuySellSplitProps, getBuySellPercentages } from './BuySellSplitBase'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const BuySellSplitDesktop = ({
  className,
  data,
  buyPercentage,
  sellPercentage,
  buyCount: legacyBuyCount,
  sellCount: legacySellCount
}: BuySellSplitProps) => {
  const buyCount = data?.buyCount ?? legacyBuyCount ?? 0
  const sellCount = data?.sellCount ?? legacySellCount ?? 0

  const { buyPct, sellPct } = useMemo(
    () => getBuySellPercentages({ buyCount, sellCount, buyPercentage, sellPercentage }),
    [buyCount, sellCount, buyPercentage, sellPercentage]
  )

  const buyStripeColor = '#1fb845'
  const sellStripeColor = '#ff540c'

  return (
    <div className={cn('relative h-full w-full flex flex-col justify-between', className)}>
      <div className="flex items-center gap-[6px]">
        <img src={arrowIcon} alt="Buy/Sell Split" className="w-4 h-4 dark:[filter:brightness(0)_invert(1)_opacity(0.72)]" />
        <span className="font-plus-jakarta font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
          Buy/Sell Split
        </span>
        <InfoTooltip
          content="Share of buy vs sell orders currently on the order book."
          ariaLabel="Buy/Sell Split info"
        />
      </div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-[4px]">
          <span className="font-plus-jakarta font-medium text-[28px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
            {buyPct}%
          </span>
          <div className="bg-[#1fb845] flex items-center justify-center px-[6px] py-[4px] rounded-[16px]">
            <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
              BUY
            </span>
          </div>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 h-[32px] w-[221px] overflow-hidden">
          <div className="flex h-full w-full">
            {buyPct > 0 && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${buyPct}%` }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <div
                  className="h-full w-full"
                  style={{
                    backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1.5px, ${buyStripeColor} 1.5px, ${buyStripeColor} 3px)`
                  }}
                />
              </motion.div>
            )}

            {sellPct > 0 && (
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${sellPct}%` }}
                transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
              >
                <div
                  className="h-full w-full"
                  style={{
                    backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1.5px, ${sellStripeColor} 1.5px, ${sellStripeColor} 3px)`
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-[4px]">
          <div className="bg-[#ff540c] flex items-center justify-center px-[6px] py-[4px] rounded-[16px]">
            <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
              SELL
            </span>
          </div>
          <span className="font-plus-jakarta font-medium text-[28px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]">
            {sellPct}%
          </span>
        </div>
      </div>
    </div>
  )
}
